import SiteCoreLinks from "@/components/SiteCoreLinks";
// /search?q= — site-wide search results page.
// Answers the SearchAction declared in WebSite schema on the root route.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { searchAll, groupByCategory, CATEGORY_ORDER, type SearchCategory } from "@/lib/search-index";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

type SearchQS = { q: string; cat: string };

export const Route = createFileRoute("/search")({
  validateSearch: (input: Record<string, unknown>): SearchQS => ({
    q: typeof input.q === "string" ? input.q : "",
    cat: typeof input.cat === "string" ? input.cat : "",
  }),
  head: ({ match }) => {
    const q = (match.search as { q?: string })?.q ?? "";
    const title = q ? `ค้นหา "${q}" — NYC Language Institute` : "ค้นหาในเว็บไซต์ — NYC Language Institute";
    const desc = q
      ? `ผลการค้นหา "${q}" สำหรับบริการแปล รับรอง Notary วีซ่า และ Apostille ในประเทศไทย`
      : "ค้นหาบริการแปลเอกสาร Notary Public Apostille รับรองสถานทูต และวีซ่าทุกประเภทในเว็บไซต์ NYC Legal";
    return {
      meta: [
        { title: ctaTitle(title, "/search", "th") },
        { name: "description", content: ctaDesc(desc, "/search", "th")},
        // Internal search pages should not be indexed independently of /
        { name: "robots", content: q ? "noindex, follow" : "index, follow" },
        { property: "og:title", content: ctaTitle(title, "/search", "th")},
        { property: "og:description", content: ctaDesc(desc, "/search", "th")},
        { property: "og:url", content: `${SITE}/search${q ? `?q=${encodeURIComponent(q)}` : ""}` },
      ],
      links: [
        { rel: "canonical", href: `${SITE}/search` },
        ...hreflangLinks("/search"),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "NYC Language Institute",
            url: SITE,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          }),
        },
      ],
    };
  },
  component: SearchPage,
});

function SearchPage() {
  const { q, cat } = Route.useSearch();
  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);

  const hits = useMemo(() => searchAll(q, 120), [q]);
  const groups = useMemo(() => groupByCategory(hits), [hits]);
  const activeCats = CATEGORY_ORDER.filter((c) => groups[c]?.length);
  const filtered = cat ? hits.filter((h) => h.category === cat) : hits;

  return (
    <main className="min-h-screen bg-background text-foreground">
      <section className="border-b border-border bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-12 text-white">
        <div className="mx-auto max-w-4xl">
          <h1 className="text-3xl font-extrabold sm:text-4xl">
            ค้นหา<span className="text-amber-300">ในเว็บไซต์</span>
          </h1>
          <p className="mt-2 text-sm text-white/70">
            ค้นบริการ · ประเทศ · ภาษา · บทความ · คำถามที่พบบ่อย — รวม {hits.length || "หลายพัน"} รายการ
          </p>
          <form
            method="get"
            action="/search"
            className="mt-5 flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3 py-2 backdrop-blur"
          >
            <SearchIcon className="h-5 w-5 text-amber-300" />
            <input
              name="q"
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="พิมพ์คำค้นหา เช่น notary, apostille, ทะเบียนสมรส, NAATI"
              className="w-full bg-transparent text-white placeholder-white/40 outline-none"
              autoFocus
            />
            <button
              type="submit"
              className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              ค้นหา
            </button>
          </form>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          {!q && <EmptyState />}

          {q && hits.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <p className="text-lg font-semibold">ไม่พบผลลัพธ์สำหรับ "{q}"</p>
              <p className="mt-2 text-sm text-muted-foreground">
                ลองใช้คำที่สั้นกว่า หรือดูบริการหลักของเราที่{" "}
                <Link to="/services" className="text-primary underline">/services</Link>
              </p>
            </div>
          )}

          {q && hits.length > 0 && (
            <>
              <div className="mb-6 flex flex-wrap gap-2">
                <CatChip current={cat} value="" label={`ทั้งหมด (${hits.length})`} q={q} />
                {activeCats.map((c) => (
                  <CatChip
                    key={c}
                    current={cat}
                    value={c}
                    label={`${labelFor(c)} (${groups[c].length})`}
                    q={q}
                  />
                ))}
              </div>

              <ol className="space-y-3">
                {filtered.map((h, i) => (
                  <li key={`${h.href}-${i}`}>
                    <Link
                      to={h.href}
                      className="group flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4 transition hover:border-primary hover:bg-accent/40"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                            {labelFor(h.category)}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">{h.href}</span>
                        </div>
                        <h3 className="mt-1 text-base font-semibold text-foreground group-hover:text-primary">
                          {h.title}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{h.description}</p>
                      </div>
                      <ArrowRight className="mt-2 h-4 w-4 flex-none text-muted-foreground group-hover:text-primary" />
                    </Link>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}

function CatChip({ current, value, label, q }: { current: string; value: string; label: string; q: string }) {
  const active = current === value;
  return (
    <Link
      to="/search"
      search={{ q, cat: value }}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:border-primary hover:text-primary"
      }`}
    >
      {label}
    </Link>
  );
}

function labelFor(c: SearchCategory): string {
  const map: Record<SearchCategory, string> = {
    Service: "บริการ",
    Notary: "Notary",
    Visa: "วีซ่า",
    Translate: "แปลภาษา",
    Apostille: "Apostille",
    Embassy: "สถานทูต",
    Marriage: "ทะเบียนสมรส",
    Location: "สาขา/พื้นที่",
    Blog: "บทความ",
    FAQ: "ถาม-ตอบ",
    Page: "หน้า",
  };
  return map[c] ?? c;
}

function EmptyState() {
  const suggestions = [
    "notary", "apostille", "NAATI", "Non-B", "LTR", "วีซ่าแต่งงาน",
    "ทะเบียนสมรส", "แปลใบเกิด", "รับรองกงสุล", "ใบหย่า",
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-8">
      <h2 className="text-lg font-semibold">ลองค้นหาคำยอดนิยม</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <Link
            key={s}
            to="/search"
            search={{ q: s, cat: "" }}
            className="rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary"
          >
            {s}
          </Link>
        ))}
      </div>
    </div>
  );
}
